/**
 * G1/G2/G3 — durable post-pay auth action claim so OIDC (full document / shop-bounce)
 * can finish link-account or refund after session hydrates on any chrome
 * (including `__platform__`).
 *
 * sessionStorage keys:
 * - pending: `customer:postPayPendingAuthAction`
 * - success outcomes: `customer:postPayAuthActionOutcome` — map keyed by
 *   `kind:paymentId` so recording B does not erase A (CTA UI sync after runner wins)
 * TTL: same 30 minutes as {@link RETURN_TARGET_TTL_MS}.
 */

import {
  RETURN_TARGET_TTL_MS,
  TENANT_CODE_REGEX,
} from '../../shared/session/returnTarget.js';

export const POST_PAY_PENDING_AUTH_ACTION_KEY = 'customer:postPayPendingAuthAction';

export type PostPayPendingLinkAccountAction = {
  kind: 'link-account';
  paymentId: string;
  tenantCode: string;
  createdAtMs: number;
};

export type PostPayPendingRefundAction = {
  kind: 'refund';
  paymentId: string;
  tenantCode: string;
  apiTenantCode?: string;
  createdAtMs: number;
};

export type PostPayPendingAuthAction =
  | PostPayPendingLinkAccountAction
  | PostPayPendingRefundAction;

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function getDefaultStorage(): StorageLike | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function isRealOrgTenantCode(tenantCode: string): boolean {
  const trimmed = tenantCode.trim();
  return (
    trimmed.length > 0 &&
    trimmed !== '__platform__' &&
    !trimmed.startsWith('__') &&
    TENANT_CODE_REGEX.test(trimmed)
  );
}

function normalizePaymentId(paymentId: string): string | null {
  const trimmed = paymentId.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function isExpired(createdAtMs: number, nowMs: number): boolean {
  return nowMs - createdAtMs > RETURN_TARGET_TTL_MS;
}

function parsePendingAuthAction(
  raw: unknown,
  nowMs: number,
): PostPayPendingAuthAction | null {
  if (raw === null || typeof raw !== 'object') {
    return null;
  }
  const row = raw as Record<string, unknown>;
  if (typeof row.kind !== 'string' || typeof row.createdAtMs !== 'number') {
    return null;
  }
  if (!Number.isFinite(row.createdAtMs) || isExpired(row.createdAtMs, nowMs)) {
    return null;
  }
  if (typeof row.paymentId !== 'string' || typeof row.tenantCode !== 'string') {
    return null;
  }
  const paymentId = normalizePaymentId(row.paymentId);
  if (paymentId === null || !isRealOrgTenantCode(row.tenantCode)) {
    return null;
  }
  const tenantCode = row.tenantCode.trim();

  if (row.kind === 'link-account') {
    return {
      kind: 'link-account',
      paymentId,
      tenantCode,
      createdAtMs: row.createdAtMs,
    };
  }

  if (row.kind === 'refund') {
    let apiTenantCode: string | undefined;
    if (typeof row.apiTenantCode === 'string') {
      const trimmedApi = row.apiTenantCode.trim();
      if (trimmedApi.length > 0) {
        if (!isRealOrgTenantCode(trimmedApi)) {
          return null;
        }
        apiTenantCode = trimmedApi;
      }
    }
    return {
      kind: 'refund',
      paymentId,
      tenantCode,
      ...(apiTenantCode !== undefined ? { apiTenantCode } : {}),
      createdAtMs: row.createdAtMs,
    };
  }

  return null;
}

function writePending(
  action: PostPayPendingAuthAction,
  storage: StorageLike | null = getDefaultStorage(),
): boolean {
  if (storage === null) {
    return false;
  }
  try {
    storage.setItem(POST_PAY_PENDING_AUTH_ACTION_KEY, JSON.stringify(action));
    return true;
  } catch {
    return false;
  }
}

/**
 * Park a link-account claim before OIDC / embedded sign-in.
 * Fail-closed: invalid ids or storage errors → `false` (nothing written).
 */
export function savePendingPostPayLinkClaim(
  input: { paymentId: string; tenantCode: string },
  storage: StorageLike | null = getDefaultStorage(),
  nowMs: number = Date.now(),
): boolean {
  const normalizedPaymentId = normalizePaymentId(input.paymentId);
  if (normalizedPaymentId === null || !isRealOrgTenantCode(input.tenantCode)) {
    return false;
  }
  return writePending(
    {
      kind: 'link-account',
      paymentId: normalizedPaymentId,
      tenantCode: input.tenantCode.trim(),
      createdAtMs: nowMs,
    },
    storage,
  );
}

/**
 * Park a refund-request claim before OIDC / embedded sign-in.
 * `apiTenantCode` is the row-tenant API override when chrome is `__platform__`.
 * Fail-closed on validation / storage errors.
 */
export function savePendingPostPayRefund(
  input: {
    paymentId: string;
    tenantCode: string;
    apiTenantCode?: string;
  },
  storage: StorageLike | null = getDefaultStorage(),
  nowMs: number = Date.now(),
): boolean {
  const normalizedPaymentId = normalizePaymentId(input.paymentId);
  if (normalizedPaymentId === null || !isRealOrgTenantCode(input.tenantCode)) {
    return false;
  }
  let normalizedApi: string | undefined;
  if (input.apiTenantCode !== undefined) {
    const trimmedApi = input.apiTenantCode.trim();
    if (trimmedApi.length > 0) {
      if (!isRealOrgTenantCode(trimmedApi)) {
        return false;
      }
      normalizedApi = trimmedApi;
    }
  }
  return writePending(
    {
      kind: 'refund',
      paymentId: normalizedPaymentId,
      tenantCode: input.tenantCode.trim(),
      ...(normalizedApi !== undefined ? { apiTenantCode: normalizedApi } : {}),
      createdAtMs: nowMs,
    },
    storage,
  );
}

/**
 * Read pending claim without consuming. Expired / corrupt / storage errors →
 * clear key and return `null` (fail closed).
 */
export function peekPendingPostPayAuthAction(
  storage: StorageLike | null = getDefaultStorage(),
  nowMs: number = Date.now(),
): PostPayPendingAuthAction | null {
  if (storage === null) {
    return null;
  }
  try {
    const raw = storage.getItem(POST_PAY_PENDING_AUTH_ACTION_KEY);
    if (raw === null || raw.length === 0) {
      return null;
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw) as unknown;
    } catch {
      storage.removeItem(POST_PAY_PENDING_AUTH_ACTION_KEY);
      return null;
    }
    const action = parsePendingAuthAction(parsed, nowMs);
    if (action === null) {
      storage.removeItem(POST_PAY_PENDING_AUTH_ACTION_KEY);
      return null;
    }
    return action;
  } catch {
    try {
      storage.removeItem(POST_PAY_PENDING_AUTH_ACTION_KEY);
    } catch {
      // ignore secondary clear failure
    }
    return null;
  }
}

export function clearPendingPostPayAuthAction(
  storage: StorageLike | null = getDefaultStorage(),
): void {
  if (storage === null) {
    return;
  }
  try {
    storage.removeItem(POST_PAY_PENDING_AUTH_ACTION_KEY);
  } catch {
    // Fail closed — ignore storage errors on clear.
  }
}

/** Session outcome after a successful runner/CTA claim (UI sync across remounts). */
export const POST_PAY_AUTH_ACTION_OUTCOME_KEY = 'customer:postPayAuthActionOutcome';

/** Same-tab notify for `useSyncExternalStore` subscribers (sessionStorage has no `storage` event). */
export const POST_PAY_AUTH_ACTION_OUTCOME_CHANGED_EVENT =
  'customer:postPayAuthActionOutcome-changed';

export type PostPayAuthActionOutcome = {
  kind: 'link-account' | 'refund';
  paymentId: string;
  status: 'succeeded';
  completedAtMs: number;
};

/** Stuck in-flight claim TTL (~60s) — expire on begin/isInFlight; watchdog clears after TTL. */
export const POST_PAY_AUTH_CLAIM_TTL_MS = 60_000;

/** In-flight claim owned by shell runner (or CTA) — blocks parallel mutate for same payment+kind. */
type PostPayAuthClaimInFlight = {
  kind: 'link-account' | 'refund';
  paymentId: string;
  startedAtMs: number;
  /** Monotonic token — stale mutate callbacks after TTL/retry must ignore settle (G2). */
  generation: number;
};

let claimInFlight: PostPayAuthClaimInFlight | null = null;
let claimWatchdogTimer: ReturnType<typeof setTimeout> | null = null;
let claimGenerationSeq = 0;

function clearClaimWatchdog(): void {
  if (claimWatchdogTimer !== null) {
    clearTimeout(claimWatchdogTimer);
    claimWatchdogTimer = null;
  }
}

function isClaimExpired(startedAtMs: number, nowMs: number): boolean {
  return nowMs - startedAtMs > POST_PAY_AUTH_CLAIM_TTL_MS;
}

/** Drop claim if past TTL (injected `nowMs` for tests; production uses `Date.now()`). */
function expireClaimIfNeeded(nowMs: number): void {
  if (claimInFlight === null) {
    return;
  }
  if (isClaimExpired(claimInFlight.startedAtMs, nowMs)) {
    clearClaimWatchdog();
    claimInFlight = null;
  }
}

function armClaimWatchdog(owner: PostPayAuthClaimInFlight): void {
  clearClaimWatchdog();
  const { kind, paymentId, startedAtMs, generation } = owner;
  claimWatchdogTimer = setTimeout((): void => {
    claimWatchdogTimer = null;
    // Clear only if the same owner is still holding the slot.
    if (
      claimInFlight !== null &&
      claimInFlight.kind === kind &&
      claimInFlight.paymentId === paymentId &&
      claimInFlight.startedAtMs === startedAtMs &&
      claimInFlight.generation === generation
    ) {
      claimInFlight = null;
    }
  }, POST_PAY_AUTH_CLAIM_TTL_MS);
}

/**
 * Begin in-flight claim. Returns claim `generation` on success, or `null` if
 * the slot is already owned (or paymentId invalid).
 */
export function beginPostPayAuthClaimInFlight(
  input: {
    kind: 'link-account' | 'refund';
    paymentId: string;
  },
  nowMs: number = Date.now(),
): number | null {
  const paymentId = normalizePaymentId(input.paymentId);
  if (paymentId === null) {
    return null;
  }
  expireClaimIfNeeded(nowMs);
  if (claimInFlight !== null) {
    // Another mutate already owns the claim slot — refuse parallel POST.
    return null;
  }
  claimGenerationSeq += 1;
  const generation = claimGenerationSeq;
  claimInFlight = {
    kind: input.kind,
    paymentId,
    startedAtMs: nowMs,
    generation,
  };
  armClaimWatchdog(claimInFlight);
  return generation;
}

/**
 * Begin in-flight claim first; only on success clear pending when it matches
 * the same kind+paymentId. Begin failure leaves pending untouched.
 * Returns claim generation, or `null` if begin failed.
 */
export function beginPostPayAuthClaimAndClearPending(
  input: {
    kind: 'link-account' | 'refund';
    paymentId: string;
  },
  storage?: StorageLike | null,
): number | null {
  const generation = beginPostPayAuthClaimInFlight(input);
  if (generation === null) {
    return null;
  }
  const pending = peekPendingPostPayAuthAction(storage);
  const paymentId = normalizePaymentId(input.paymentId);
  if (
    pending !== null &&
    paymentId !== null &&
    pending.kind === input.kind &&
    pending.paymentId === paymentId
  ) {
    clearPendingPostPayAuthAction(storage);
  }
  return generation;
}

export function endPostPayAuthClaimInFlight(input?: {
  kind: 'link-account' | 'refund';
  paymentId: string;
}): void {
  if (input === undefined) {
    clearClaimWatchdog();
    claimInFlight = null;
    return;
  }
  const paymentId = normalizePaymentId(input.paymentId);
  if (
    claimInFlight !== null &&
    paymentId !== null &&
    claimInFlight.paymentId === paymentId &&
    claimInFlight.kind === input.kind
  ) {
    clearClaimWatchdog();
    claimInFlight = null;
  }
}

export function peekPostPayAuthClaimInFlight(
  nowMs: number = Date.now(),
): PostPayAuthClaimInFlight | null {
  expireClaimIfNeeded(nowMs);
  return claimInFlight;
}

export function isPostPayAuthClaimInFlightFor(
  input: {
    kind: 'link-account' | 'refund';
    paymentId: string;
  },
  nowMs: number = Date.now(),
): boolean {
  expireClaimIfNeeded(nowMs);
  const paymentId = normalizePaymentId(input.paymentId);
  return (
    claimInFlight !== null &&
    paymentId !== null &&
    claimInFlight.paymentId === paymentId &&
    claimInFlight.kind === input.kind
  );
}

/**
 * True only when the in-flight claim matches kind + paymentId + generation
 * (after TTL expire). Watchdog/TTL nulling the claim makes stale generations fail.
 */
export function isPostPayAuthClaimGenerationCurrent(
  input: {
    kind: 'link-account' | 'refund';
    paymentId: string;
    generation: number;
  },
  nowMs: number = Date.now(),
): boolean {
  expireClaimIfNeeded(nowMs);
  const paymentId = normalizePaymentId(input.paymentId);
  return (
    claimInFlight !== null &&
    paymentId !== null &&
    claimInFlight.paymentId === paymentId &&
    claimInFlight.kind === input.kind &&
    claimInFlight.generation === input.generation
  );
}

function notifyPostPayAuthActionOutcomeChanged(): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(new Event(POST_PAY_AUTH_ACTION_OUTCOME_CHANGED_EVENT));
}

/** Subscribe to same-tab outcome writes (record/clear). */
export function subscribePostPayAuthActionOutcome(onChange: () => void): () => void {
  if (typeof window === 'undefined') {
    return (): void => undefined;
  }
  window.addEventListener(POST_PAY_AUTH_ACTION_OUTCOME_CHANGED_EVENT, onChange);
  return (): void => {
    window.removeEventListener(POST_PAY_AUTH_ACTION_OUTCOME_CHANGED_EVENT, onChange);
  };
}

/** Snapshot for `useSyncExternalStore` — raw sessionStorage string (stable identity). */
export function getPostPayAuthActionOutcomeSnapshot(): string {
  if (typeof window === 'undefined') {
    return '';
  }
  try {
    return window.sessionStorage.getItem(POST_PAY_AUTH_ACTION_OUTCOME_KEY) ?? '';
  } catch {
    return '';
  }
}

export function getPostPayAuthActionOutcomeServerSnapshot(): string {
  return '';
}

export type PostPayAuthActionOutcomeKey = {
  kind: 'link-account' | 'refund';
  paymentId: string;
};

/** Map key — `kind:paymentId` so distinct outcomes coexist. */
export function buildPostPayAuthActionOutcomeKey(
  kind: 'link-account' | 'refund',
  paymentId: string,
): string {
  return `${kind}:${paymentId}`;
}

type PostPayAuthActionOutcomeMap = Record<string, PostPayAuthActionOutcome>;

function parseOutcome(
  raw: unknown,
  nowMs: number,
): PostPayAuthActionOutcome | null {
  if (raw === null || typeof raw !== 'object') {
    return null;
  }
  const row = raw as Record<string, unknown>;
  if (
    (row.kind !== 'link-account' && row.kind !== 'refund') ||
    typeof row.paymentId !== 'string' ||
    row.status !== 'succeeded' ||
    typeof row.completedAtMs !== 'number' ||
    !Number.isFinite(row.completedAtMs)
  ) {
    return null;
  }
  if (isExpired(row.completedAtMs, nowMs)) {
    return null;
  }
  const paymentId = normalizePaymentId(row.paymentId);
  if (paymentId === null) {
    return null;
  }
  return {
    kind: row.kind,
    paymentId,
    status: 'succeeded',
    completedAtMs: row.completedAtMs,
  };
}

function isLegacySingleOutcomeRow(raw: unknown): boolean {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    return false;
  }
  const row = raw as Record<string, unknown>;
  return (
    (row.kind === 'link-account' || row.kind === 'refund') &&
    typeof row.paymentId === 'string' &&
    row.status === 'succeeded'
  );
}

/**
 * Load outcome map; migrate legacy single-outcome JSON; prune expired entries.
 * Returns null when storage is missing/unusable.
 */
function readOutcomeMap(
  storage: StorageLike,
  nowMs: number,
): { map: PostPayAuthActionOutcomeMap; dirty: boolean } | null {
  try {
    const raw = storage.getItem(POST_PAY_AUTH_ACTION_OUTCOME_KEY);
    if (raw === null || raw.length === 0) {
      return { map: {}, dirty: false };
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw) as unknown;
    } catch {
      return { map: {}, dirty: true };
    }

    let map: PostPayAuthActionOutcomeMap = {};
    let dirty = false;

    if (isLegacySingleOutcomeRow(parsed)) {
      const legacy = parseOutcome(parsed, nowMs);
      if (legacy !== null) {
        map[buildPostPayAuthActionOutcomeKey(legacy.kind, legacy.paymentId)] =
          legacy;
      }
      dirty = true;
    } else if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const envelope = parsed as Record<string, unknown>;
      // Flat `{ "kind:paymentId": Outcome }` or `{ v: 1, entries: Record }`.
      let rows: Record<string, unknown>;
      if (
        envelope.v === 1 &&
        envelope.entries !== null &&
        typeof envelope.entries === 'object' &&
        !Array.isArray(envelope.entries)
      ) {
        rows = envelope.entries as Record<string, unknown>;
        dirty = true; // normalize to flat map on next write
      } else {
        rows = envelope;
      }
      for (const [key, value] of Object.entries(rows)) {
        const outcome = parseOutcome(value, nowMs);
        if (outcome === null) {
          dirty = true;
          continue;
        }
        const expectedKey = buildPostPayAuthActionOutcomeKey(
          outcome.kind,
          outcome.paymentId,
        );
        if (key !== expectedKey) {
          dirty = true;
        }
        map[expectedKey] = outcome;
      }
    } else {
      return { map: {}, dirty: true };
    }

    return { map, dirty };
  } catch {
    return null;
  }
}

function writeOutcomeMap(
  storage: StorageLike,
  map: PostPayAuthActionOutcomeMap,
): boolean {
  try {
    if (Object.keys(map).length === 0) {
      storage.removeItem(POST_PAY_AUTH_ACTION_OUTCOME_KEY);
    } else {
      storage.setItem(POST_PAY_AUTH_ACTION_OUTCOME_KEY, JSON.stringify(map));
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Record successful claim/refund so remounted/already-mounted CTAs can show
 * linked/submitted after the shell runner wins (pending already cleared).
 * Merges into `kind:paymentId` map — does not erase other outcomes.
 * Notifies same-tab subscribers.
 */
export function recordPostPayAuthActionOutcome(
  input: PostPayAuthActionOutcomeKey,
  storage: StorageLike | null = getDefaultStorage(),
  nowMs: number = Date.now(),
): boolean {
  const paymentId = normalizePaymentId(input.paymentId);
  if (paymentId === null || storage === null) {
    return false;
  }
  const loaded = readOutcomeMap(storage, nowMs);
  if (loaded === null) {
    return false;
  }
  const outcome: PostPayAuthActionOutcome = {
    kind: input.kind,
    paymentId,
    status: 'succeeded',
    completedAtMs: nowMs,
  };
  const map = { ...loaded.map };
  map[buildPostPayAuthActionOutcomeKey(input.kind, paymentId)] = outcome;
  if (!writeOutcomeMap(storage, map)) {
    return false;
  }
  notifyPostPayAuthActionOutcomeChanged();
  return true;
}

function persistDirtyOutcomeMap(
  storage: StorageLike,
  loaded: { map: PostPayAuthActionOutcomeMap; dirty: boolean },
): void {
  if (!loaded.dirty) {
    return;
  }
  if (!writeOutcomeMap(storage, loaded.map)) {
    try {
      storage.removeItem(POST_PAY_AUTH_ACTION_OUTCOME_KEY);
    } catch {
      // ignore
    }
  }
  notifyPostPayAuthActionOutcomeChanged();
}

/**
 * Peek most-recent outcome (single-payment / legacy tests) or one keyed entry.
 * Prefer {@link hasSucceededPostPayAuthOutcome} for payment-scoped boolean checks.
 */
export function peekPostPayAuthActionOutcome(
  storageOrKey?: StorageLike | null | PostPayAuthActionOutcomeKey,
  nowMsOrStorage?: number | StorageLike | null,
  nowMsArg: number = Date.now(),
): PostPayAuthActionOutcome | null {
  let storage: StorageLike | null;
  let nowMs: number;
  let filter: PostPayAuthActionOutcomeKey | undefined;

  if (
    storageOrKey !== null &&
    storageOrKey !== undefined &&
    typeof storageOrKey === 'object' &&
    'kind' in storageOrKey &&
    'paymentId' in storageOrKey
  ) {
    filter = storageOrKey;
    storage =
      typeof nowMsOrStorage === 'object' || nowMsOrStorage === null
        ? (nowMsOrStorage ?? getDefaultStorage())
        : getDefaultStorage();
    nowMs =
      typeof nowMsOrStorage === 'number' ? nowMsOrStorage : nowMsArg;
  } else {
    storage =
      storageOrKey === undefined ? getDefaultStorage() : (storageOrKey as StorageLike | null);
    nowMs = typeof nowMsOrStorage === 'number' ? nowMsOrStorage : Date.now();
  }

  if (storage === null) {
    return null;
  }
  const loaded = readOutcomeMap(storage, nowMs);
  if (loaded === null) {
    return null;
  }
  persistDirtyOutcomeMap(storage, loaded);

  if (filter !== undefined) {
    const paymentId = normalizePaymentId(filter.paymentId);
    if (paymentId === null) {
      return null;
    }
    return (
      loaded.map[buildPostPayAuthActionOutcomeKey(filter.kind, paymentId)] ??
      null
    );
  }

  let latest: PostPayAuthActionOutcome | null = null;
  for (const outcome of Object.values(loaded.map)) {
    if (latest === null || outcome.completedAtMs >= latest.completedAtMs) {
      latest = outcome;
    }
  }
  return latest;
}

/**
 * Clear whole outcome map (default / tests), or one `kind`+`paymentId` entry.
 */
export function clearPostPayAuthActionOutcome(
  storage: StorageLike | null = getDefaultStorage(),
  key?: PostPayAuthActionOutcomeKey,
  nowMs: number = Date.now(),
): void {
  if (storage === null) {
    return;
  }
  try {
    if (key === undefined) {
      storage.removeItem(POST_PAY_AUTH_ACTION_OUTCOME_KEY);
      notifyPostPayAuthActionOutcomeChanged();
      return;
    }
    const paymentId = normalizePaymentId(key.paymentId);
    if (paymentId === null) {
      return;
    }
    const loaded = readOutcomeMap(storage, nowMs);
    if (loaded === null) {
      return;
    }
    const map = { ...loaded.map };
    delete map[buildPostPayAuthActionOutcomeKey(key.kind, paymentId)];
    if (!writeOutcomeMap(storage, map)) {
      return;
    }
    notifyPostPayAuthActionOutcomeChanged();
  } catch {
    // ignore
  }
}

/** True when durable outcome already succeeded for this payment + kind. */
export function hasSucceededPostPayAuthOutcome(
  input: PostPayAuthActionOutcomeKey,
  storage: StorageLike | null = getDefaultStorage(),
  nowMs: number = Date.now(),
): boolean {
  const outcome = peekPostPayAuthActionOutcome(input, storage, nowMs);
  return outcome !== null && outcome.status === 'succeeded';
}

/** Test seam — wipe pending claim + success outcome + in-flight + generation seq. */
export function resetPendingPostPayAuthActionForTests(
  storage: StorageLike | null = getDefaultStorage(),
): void {
  clearPendingPostPayAuthAction(storage);
  clearPostPayAuthActionOutcome(storage);
  clearClaimWatchdog();
  claimInFlight = null;
  claimGenerationSeq = 0;
}
